import { type CreatePackageFaqRequest, type PackageFaqOut } from '@utazzo/shared';
import { UzForm, UzFormField, UzInput } from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { PackageFormPanel } from './PackageFormPanel';

import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';

interface FormValues {
  question: string;
  answerHtml: string;
}

interface Props {
  mode: 'create' | 'edit';
  item?: PackageFaqOut;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreatePackageFaqRequest) => Promise<void>;
}

const toDefaults = (mode: 'create' | 'edit', item: PackageFaqOut | undefined): FormValues =>
  mode === 'edit' && item
    ? { question: item.question, answerHtml: item.answerHtml }
    : { question: '', answerHtml: '' };

export const PackageFaqFormDrawer = ({
  mode,
  item,
  pending,
  onClose,
  onSubmit,
}: Props): React.JSX.Element => {
  const form = useForm<FormValues>({ defaultValues: toDefaults(mode, item) });

  useEffect(() => {
    form.reset(toDefaults(mode, item));
  }, [mode, item, form]);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit({
      question: values.question.trim(),
      answerHtml: values.answerHtml.trim(),
    });
  });

  return (
    <PackageFormPanel
      title={mode === 'create' ? 'Add FAQ' : 'Edit FAQ'}
      description="Frequently asked question and its answer for this package."
      pending={pending}
      submitLabel={mode === 'create' ? 'Add FAQ' : 'Save changes'}
      onClose={onClose}
      onSubmit={submit}
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <UzFormField name="question">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={field.value as string}
                onChange={(e) => field.onChange(e.target.value)}
                label="Question"
                placeholder="e.g. What is included in the package?"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="answerHtml">
            {({ field }) => (
              <RichTextEditor
                value={field.value as string}
                onChange={field.onChange}
                label="Answer"
                placeholder="Enter the answer..."
                folder="packages"
                stickyTop="flush"
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzForm>
    </PackageFormPanel>
  );
};
